import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Factory, Cpu, Zap, Shield, CheckCircle, Info, ChevronRight, ChevronLeft } from 'lucide-react';
import { otScan } from '../../api/client';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const OTScanWizard = ({ isOpen, onClose, onComplete }) => {
  // Get active workspace from context
  const { activeWorkspace } = useWorkspace();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [riskWarning, setRiskWarning] = useState('');
  const [formData, setFormData] = useState({
    name: '', // Optional scan name
    target: '',
    workspace_id: '', // Will be auto-set from context
    protocols: ['modbus', 's7'],
    scan_type: 'discovery',
    accept_risk: false
  });

  // Auto-set workspace_id from active workspace
  useEffect(() => {
    if (activeWorkspace?.id && !formData.workspace_id) {
      setFormData(prev => ({ ...prev, workspace_id: activeWorkspace.id }));
    }
  }, [activeWorkspace?.id]);

  useEffect(() => {
    if (isOpen) {
      fetchProtocols();
      fetchRiskWarning();
    }
  }, [isOpen]);

  const fetchProtocols = async () => {
    try {
      const data = await otScan.getProtocols();
      setProtocols(data);
    } catch (error) {
      console.error('Failed to fetch OT protocols:', error);
      // Fallback protocols
      setProtocols([
        { name: 'modbus', ports: [502], risk: 'high', description: 'Modbus TCP - PLCs industriels', implemented: true },
        { name: 's7', ports: [102], risk: 'critical', description: 'Siemens S7comm - Automates Siemens', implemented: true },
        { name: 'dnp3', ports: [20000], risk: 'critical', description: 'DNP3 - SCADA utilities', implemented: true },
        { name: 'bacnet', ports: [47808], risk: 'medium', description: 'BACnet/IP - GTB', implemented: true },
        { name: 'opcua', ports: [4840], risk: 'high', description: 'OPC UA - Automation', implemented: true },
        { name: 'ethernetip', ports: [44818], risk: 'high', description: 'EtherNet/IP - Allen-Bradley', implemented: false },
        { name: 'profinet', ports: [34962], risk: 'critical', description: 'Profinet - Siemens Ethernet', implemented: false },
        { name: 'iec104', ports: [2404], risk: 'critical', description: 'IEC 60870-5-104 - SCADA Power', implemented: false },
      ]);
    }
  };

  const fetchRiskWarning = async () => {
    try {
      const data = await otScan.getRiskWarning();
      setRiskWarning(data.warning);
    } catch (error) {
      setRiskWarning(`ATTENTION: RISQUES DU SCAN OT/ICS/SCADA

Le scan de systèmes de contrôle industriel peut avoir des conséquences sérieuses:

1. ARRÊT DE PRODUCTION: Les scans peuvent provoquer des états de faute sur les automates (PLCs, RTUs) et arrêter les processus industriels.

2. SYSTÈMES DE SÉCURITÉ: Les systèmes instrumentés de sécurité (SIS) peuvent être affectés, compromettant la sécurité physique.

3. PERTES FINANCIÈRES: Des comportements inattendus des équipements peuvent entraîner des arrêts de production coûteux.

4. DOMMAGES MATÉRIELS: Certains équipements anciens peuvent ne pas supporter le trafic réseau inattendu.

RECOMMANDATIONS:
- Scanner uniquement pendant les fenêtres de maintenance
- Avoir des procédures de rollback prêtes
- Surveiller tous les systèmes scannés en temps réel
- Commencer par une découverte passive avant un scan actif
- Obtenir l'autorisation des propriétaires des actifs
- Avoir du personnel de sécurité en alerte

En continuant, vous confirmez avoir l'autorisation et comprendre les risques.`);
    }
  };

  const toggleProtocol = (protocolName) => {
    setFormData(prev => ({
      ...prev,
      protocols: prev.protocols.includes(protocolName)
        ? prev.protocols.filter(p => p !== protocolName)
        : [...prev.protocols, protocolName]
    }));
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'critical': return 'text-purple-400 border-purple-500 bg-purple-500/10';
      case 'high': return 'text-red-400 border-red-500 bg-red-500/10';
      case 'medium': return 'text-orange-400 border-orange-500 bg-orange-500/10';
      case 'low': return 'text-yellow-400 border-yellow-500 bg-yellow-500/10';
      default: return 'text-gray-400 border-gray-500 bg-gray-500/10';
    }
  };

  const handleSubmit = async () => {
    if (!formData.accept_risk) {
      alert('Vous devez accepter les risques pour continuer');
      return;
    }

    setLoading(true);
    try {
      const result = await otScan.create(formData);
      onComplete(result);
      onClose();
    } catch (error) {
      console.error('Failed to create OT scan:', error);
      // Check for insufficient credits error (402)
      if (error.response?.status === 402) {
        const detail = error.response?.data?.detail;
        if (detail?.error === 'insufficient_credits') {
          alert(`Crédits insuffisants pour lancer ce scan.\n\nCrédits requis: ${detail.message || 'N/A'}\n\nVeuillez acheter des crédits ou passer à un plan supérieur.`);
        } else {
          alert('Crédits insuffisants. Veuillez acheter des crédits pour continuer.');
        }
      } else {
        alert('Erreur lors de la création du scan OT: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-brand-black-light border border-orange-500/50 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-900/50 to-red-900/50 border-b border-orange-500/30 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-orange-500/20 rounded-xl flex items-center justify-center border border-orange-500/50">
                <Factory className="w-8 h-8 text-orange-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  OT/ICS/SCADA Scan
                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded border border-red-500/50">
                    PRODUCTION RISK
                  </span>
                </h2>
                <p className="text-orange-300/80 text-sm mt-1">Scanner les reseaux industriels avec precaution</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-6">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  step >= s ? 'bg-orange-500 text-white' : 'bg-gray-700 text-gray-400'
                }`}>
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-orange-500' : 'bg-gray-700'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-250px)]">
          {/* Step 1: Risk Warning */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-10 h-10 text-red-400 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-red-400 mb-4">AVERTISSEMENT CRITIQUE</h3>
                    <pre className="text-red-200/80 whitespace-pre-wrap text-sm font-sans leading-relaxed">
                      {riskWarning}
                    </pre>
                  </div>
                </div>
              </div>

              <div className="bg-orange-900/20 border border-orange-500/30 rounded-xl p-4">
                <h4 className="text-orange-400 font-semibold mb-3">Conformite NIS2 / IEC 62443</h4>
                <ul className="text-gray-300 text-sm space-y-2">
                  <li className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-orange-400" />
                    Les scans OT sont audites pour conformite NIS2
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-orange-400" />
                    Evaluation du niveau IEC 62443 (SL0-SL4)
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-orange-400" />
                    Mapping MITRE ATT&CK for ICS automatique
                  </li>
                </ul>
              </div>

              <label className="flex items-center gap-3 p-4 bg-brand-black rounded-lg border border-gray-700 cursor-pointer hover:border-orange-500/50 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.accept_risk}
                  onChange={(e) => setFormData(prev => ({ ...prev, accept_risk: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                />
                <span className="text-white">
                  J'ai lu et j'accepte les risques associés au scan OT/ICS/SCADA.
                  J'ai l'autorisation légale de scanner ces systèmes.
                </span>
              </label>
            </div>
          )}

          {/* Step 2: Target & Protocols */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Scan Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nom du scan (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Audit SCADA Usine 1, Test OT Production..."
                  className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Donnez un nom personnalisé pour identifier facilement ce scan
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Cible(s) - IP, CIDR ou plage
                </label>
                <input
                  type="text"
                  value={formData.target}
                  onChange={(e) => setFormData(prev => ({ ...prev, target: e.target.value }))}
                  placeholder="192.168.1.0/24 ou 192.168.1.1-50"
                  className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formats supportes: IP unique, CIDR (10.0.0.0/24), plage (10.0.0.1-100), liste (IP1,IP2,IP3)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Protocoles OT a scanner
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {protocols.map((protocol) => (
                    <button
                      key={protocol.name}
                      onClick={() => protocol.implemented && toggleProtocol(protocol.name)}
                      disabled={!protocol.implemented}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        !protocol.implemented
                          ? 'opacity-50 cursor-not-allowed border-gray-700 bg-gray-800/50'
                          : formData.protocols.includes(protocol.name)
                            ? 'border-orange-500 bg-orange-500/20'
                            : 'border-gray-700 hover:border-orange-500/50 bg-brand-black'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-white uppercase">{protocol.name}</span>
                        <span className={`px-2 py-0.5 text-xs rounded border ${getRiskColor(protocol.risk)}`}>
                          {protocol.risk}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400">{protocol.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Ports: {protocol.ports.join(', ')}
                        {!protocol.implemented && ' - Non implémenté'}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Type de scan
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'discovery', label: 'Découverte', desc: 'Détection des équipements', icon: Cpu },
                    { value: 'enumeration', label: 'Énumération', desc: 'Détails des équipements', icon: Info },
                    { value: 'vulnerability', label: 'Vulnérabilités', desc: 'Analyse sécurité complète', icon: Zap },
                  ].map(({ value, label, desc, icon: Icon }) => (
                    <button
                      key={value}
                      onClick={() => setFormData(prev => ({ ...prev, scan_type: value }))}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        formData.scan_type === value
                          ? 'border-orange-500 bg-orange-500/20'
                          : 'border-gray-700 hover:border-orange-500/50 bg-brand-black'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${formData.scan_type === value ? 'text-orange-400' : 'text-gray-400'}`} />
                      <div className="font-semibold text-white">{label}</div>
                      <p className="text-xs text-gray-400 mt-1">{desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-brand-black rounded-xl border border-gray-700 overflow-hidden">
                <div className="bg-gray-800/50 px-4 py-3 border-b border-gray-700">
                  <h3 className="font-semibold text-white">Résumé du Scan OT</h3>
                </div>
                <div className="p-4 space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Cible</span>
                    <span className="text-white font-mono">{formData.target || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Protocoles</span>
                    <div className="flex flex-wrap gap-2 justify-end">
                      {formData.protocols.map(p => (
                        <span key={p} className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded border border-orange-500/50 uppercase">
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Type de scan</span>
                    <span className="text-white capitalize">{formData.scan_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Risques acceptés</span>
                    <span className={formData.accept_risk ? 'text-green-400' : 'text-red-400'}>
                      {formData.accept_risk ? 'Oui' : 'Non'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-200/80">
                    <p className="font-semibold mb-1">Dernier rappel de sécurité</p>
                    <p>Assurez-vous que les systèmes de production critiques sont surveillés et que vous avez un plan de rollback en cas de problème.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-700 p-4 bg-gray-900/50 flex justify-between">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="px-6 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            {step === 1 ? 'Annuler' : 'Retour'}
          </button>

          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && !formData.accept_risk}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Suivant
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.target || formData.protocols.length === 0}
              className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Lancement...
                </>
              ) : (
                <>
                  <Factory className="w-4 h-4" />
                  Lancer le Scan OT
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OTScanWizard;
